import React, { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, Tooltip } from 'recharts';
import { ENROLL_DATA, COMPLIANCE_DATA, DEVICES } from '../data/mockData';
import { Card, CardHeader, ChartTooltip, Badge, StatCard, toast } from '../components/shared';
import { Ic, ICONS } from '../data/icons';

export default function Reports({ C, isMobile }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (type) => {
    setExporting(true);
    await new Promise(r => setTimeout(r, 900));
    setExporting(false);
    toast.success(`${type} raporu hazırlandı — FAZ 6'da gerçek dışa aktarım gelecek`);
  };

  const online    = DEVICES.filter(d=>d.status==="online").length;
  const compliant = DEVICES.filter(d=>d.compliance==="compliant").length;
  const breached  = DEVICES.filter(d=>d.compliance==="breach").length;

  const PLATFORM_DATA = [
    { name:"macOS",      value:89, color:"#3b82f6" },
    { name:"Windows",    value:84, color:"#22d3ee" },
    { name:"iOS/iPadOS", value:74, color:"#a78bfa" },
  ];

  const TREND_DATA = [
    { mon:"Oca", devices:220 }, { mon:"Şub", devices:228 }, { mon:"Mar", devices:235 },
    { mon:"Nis", devices:240 }, { mon:"May", devices:238 }, { mon:"Haz", devices:247 },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Stat cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <StatCard label="Toplam Cihaz"    value={DEVICES.length} icon="devices" color="#3b82f6" bg="rgba(59,130,246,0.12)"  C={C} />
        <StatCard label="Uyumluluk Oranı" value={`%${Math.round(compliant/DEVICES.length*1000)/10}`} icon="shield" color={C.green} bg={C.greenBg} C={C} />
        <StatCard label="Aktif İhlal"     value={breached}       icon="alert"   color={C.red}   bg={C.redBg}   C={C} />
        <StatCard label="Son 7 Gün Kayıt" value={32}             icon="up"      color={C.amber} bg={C.amberBg} C={C} />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"minmax(0,1.6fr) minmax(0,1.2fr)", gap:16 }}>
        {/* Enrollment */}
        <Card C={C}>
          <CardHeader C={C} title="Kayıt Aktivitesi" action={<Badge color={C.accent}>Son 7 gün</Badge>} />
          <div style={{ padding:"12px 8px 14px" }}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={ENROLL_DATA} margin={{ top:5,right:10,left:-20,bottom:0 }}>
                <defs>
                  <linearGradient id="rg1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.accent} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={C.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey="day" tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tooltip content={<ChartTooltip />} />} />
                <Area type="monotone" dataKey="kayıt" stroke={C.accent} strokeWidth={2} fill="url(#rg1)" dot={{ r:3,fill:C.accent }} />
                <Area type="monotone" dataKey="çıkış" stroke={C.red}    strokeWidth={2} fill="none"       dot={{ r:3,fill:C.red }} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Compliance */}
        <Card C={C}>
          <CardHeader C={C} title="Uyumluluk Dağılımı" />
          <div style={{ padding:"12px 8px 14px" }}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={COMPLIANCE_DATA} margin={{ top:5,right:10,left:-20,bottom:0 }} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey="h" tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tooltip content={<ChartTooltip />} />} />
                <Bar dataKey="uyumlu" name="Uyumlu" fill={C.green} radius={[4,4,0,0]} />
                <Bar dataKey="uyarı"  name="Uyarı"  fill={C.amber} radius={[4,4,0,0]} />
                <Bar dataKey="ihlal"  name="İhlal"  fill={C.red}   radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"minmax(0,1.2fr) minmax(0,1fr)", gap:16 }}>
        {/* Trend */}
        <Card C={C}>
          <CardHeader C={C} title="Cihaz Trendi (6 Ay)" />
          <div style={{ padding:"12px 8px 14px" }}>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={TREND_DATA} margin={{ top:5,right:10,left:-20,bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey="mon" tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:C.textFaint,fontSize:11 }} axisLine={false} tickLine={false} domain={[210,260]} />
                <Tooltip content={<Tooltip content={<ChartTooltip />} />} />
                <Line type="monotone" dataKey="devices" name="Cihazlar" stroke={C.accent} strokeWidth={2.5} dot={{ r:4,fill:C.accent }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Platform breakdown */}
        <Card C={C}>
          <CardHeader C={C} title="Platform Dağılımı" />
          <div style={{ padding:"14px 20px", display:"flex", flexDirection:"column", gap:12 }}>
            {PLATFORM_DATA.map(p => (
              <div key={p.name}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:13 }}>
                  <span style={{ color:C.textSub }}>{p.name}</span>
                  <span style={{ fontWeight:700, color:C.text }}>{p.value}</span>
                </div>
                <div style={{ height:6, borderRadius:3, background:C.border2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${Math.round(p.value/247*100)}%`, background:p.color, borderRadius:3, transition:"width 1s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Export */}
      <Card C={C}>
        <CardHeader C={C} title="Rapor Dışa Aktarımı" />
        <div style={{ padding:"16px 20px", display:"flex", flexWrap:"wrap", gap:12, alignItems:"center" }}>
          <div style={{ fontSize:13, color:C.textSub, flex:"1 1 200px" }}>
            Uyumluluk, kayıt aktivitesi ve cihaz envanterini dışa aktarın.{" "}
            <span style={{ color:C.textFaint, fontSize:12 }}>CSV ve PDF formatları FAZ 6'da backend API ile gerçekleşecek.</span>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => handleExport("CSV")} disabled={exporting} style={{ padding:"8px 16px", borderRadius:9, border:`1px solid ${C.border}`, background:"transparent", color:C.textSub, fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:7 }}>
              <Ic d={ICONS.download} size={14} /> CSV
            </button>
            <button onClick={() => handleExport("PDF")} disabled={exporting} style={{ padding:"8px 18px", borderRadius:9, border:"none", background:C.accent, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:7, opacity:exporting?0.7:1 }}>
              <Ic d={ICONS.download} size={14} s="#fff" /> {exporting?"Hazırlanıyor…":"PDF İndir"}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
